/**
 * The admin's half of the session bridge (`the-session-crosses-to-tutero-com`): a staff
 * member signed in on the teacher's app arrives here signed in, without typing a password
 * twice, and signing out here signs them out of both hosts.
 *
 * The cookie is host-only, so this host holds a session of its own or none, and it cannot
 * read the partner's — same registrable domain or not. The four routes are the auth
 * Worker's, forwarded to it by this app's own Worker (`deploy/worker.ts`); everything
 * between the bounce below and coming back is redirects the browser follows by itself:
 *
 *   start    here — remembers this browser, and sends it next door
 *   mint     next door — reads its own cookie, answers a one-time code in the fragment
 *   land     here — burns the code, sets this host's own cookie, returns to `returnTo`
 *   sign-out either — clears here, then there
 *
 * Nothing here holds, reads or passes a credential: the code travels in the fragment
 * between two of the auth Worker's own routes and page script never sees it. What this
 * module decides is whether to ask at all, which is where every trap is — 3.0 found them
 * all and `schools-app` `lib/src/presentation/utils/session_handoff{,_url}.dart` is the
 * implementation this is ported from.
 *
 * - **Ask once.** A marker on the address we come back to, so a failed round trip cannot
 *   immediately start another, AND a timestamp in `localStorage`, so a staff member who is
 *   signed out everywhere does not pay a redirect chain per page load.
 * - **`localStorage`, not a per-tab flag.** 3.0 tried the flag: a tab that asked once
 *   while signed out never asked again, so signing in next door left that tab stranded
 *   until it was closed — and every new tab paid a round trip seconds after the last one
 *   learned there was no session to fetch.
 * - **Deep links survive**: `returnTo` carries the path actually asked for.
 * - **Never when already signed in**, and never on the one address that draws a form of
 *   its own (Request Access). Sign-in here is a state the shell draws rather than a path
 *   of its own, which is why there is no second address to exclude.
 * - **Sign-out is a chain** (D6): a cookie can only be cleared by the host that set it, so
 *   each hop clears first-party and redirects on. One-way is broken by design — the next
 *   visit pulls the session straight back and there is no way out but clearing cookies by
 *   hand.
 */

export const START_PATH = '/api/auth/cross/start';
export const SIGN_OUT_PATH = '/api/auth/cross/sign-out';

/**
 * The marker on the address the bridge returns to. The landing page returns to `returnTo`
 * exactly as given, so the marker rides in that path rather than being added on arrival.
 */
export const BOUNCED = 'bridged';
export const BOUNCED_VALUE = 'done';

/** When the partner was last asked. A contract with nothing else; this app writes it alone. */
export const ATTEMPT_KEY = 'tutero.admin.cross.attempted';

/**
 * How long a fruitless attempt suppresses the next one. 3.0's thirty minutes, for 3.0's
 * reason: long enough that a signed-out visitor clicking around does not pay a round trip
 * per page load, short enough that somebody who signs in next door mid-session is picked
 * up without having to find a fresh tab.
 */
export const RETRY_AFTER_MS = 30 * 60 * 1000;

/**
 * How long to wait for a bounce to commit before drawing the form instead.
 *
 * A host the pairing does not name answers `start` with a 204, and a 204 to a top-level
 * navigation leaves the browser exactly where it was — so without this, a build that turns
 * the bridge on before the pairing names it strands a staff member on a page that is
 * waiting for a navigation that already happened and did nothing.
 */
export const STRANDED_MS = 5_000;

/**
 * Whether a recorded attempt should stop us asking again.
 *
 * Anything unparseable counts as expired rather than suppressing, and so does a timestamp
 * in the future — a clock moved backwards must not lock the bridge for half an hour. An
 * empty value reads as the epoch, which is always stale, which is what retires 3.0's
 * untimestamped `'1'` marker too.
 */
export function attemptSuppresses(stored: string | null, now: number): boolean {
  if (stored === null) return false;
  const at = Number(stored);
  if (!Number.isFinite(at)) return false;
  const age = now - at;
  return age >= 0 && age < RETRY_AFTER_MS;
}

/**
 * The path to come back to: the one asked for, with the marker set and never doubled.
 *
 * A path and a query, never a URL — the auth Worker refuses anything that does not begin
 * with a single `/` and rebuilds the origin itself, so a full URL here would be dropped
 * for `/` and the deep link lost. The fragment is dropped too: the landing page uses the
 * fragment for the code.
 */
export function returnPath(at: { pathname: string; search: string }): string {
  const params = new URLSearchParams(at.search);
  params.delete(BOUNCED);
  params.set(BOUNCED, BOUNCED_VALUE);
  /* `//foo` is not a path the auth Worker will take, and it is what a typed address gives. */
  const path = `/${at.pathname.replace(/^\/+/, '')}`;
  return `${path}?${params.toString()}`;
}

/** `start` on this origin, carrying the path to come back to. */
export function startUrl(origin: string, returnTo: string): string {
  return `${origin}${START_PATH}?returnTo=${encodeURIComponent(returnTo)}`;
}

/**
 * `sign-out` on the origin the chain STARTS on, asking for the chain.
 *
 * Not this origin: the chain always ends on the other host, so it starts on the partner
 * to end here (`crossSignOut`).
 *
 * `next=partner` is the only onward destination the auth Worker will take, and it builds
 * it from the pairing rather than from this URL. `returnTo` is where a host with no
 * partner lands instead — on the origin named here, not on the admin, so it is the
 * partner's root and the same place sign-out landed before this change.
 */
export function signOutUrl(origin: string, returnTo = '/'): string {
  return `${origin}${SIGN_OUT_PATH}?next=partner&returnTo=${encodeURIComponent(returnTo)}`;
}

/** What a signed-out arrival is, as the shell reads it off the page. */
export interface Arrival {
  /** `VITE_SESSION_BRIDGE`: this host serves its own `/api/auth/*` and the pairing names it. */
  bridged: boolean;
  /** The address asked for. */
  pathname: string;
  search: string;
  /** This page's origin: the bounce is a path on it and nowhere else. */
  origin: string;
  /** Request Access draws a form of its own, so it never bounces. */
  requesting: boolean;
  /** `ATTEMPT_KEY` as it stands, and the clock to read it against. */
  attempted: string | null;
  now: number;
}

/** Where a signed-out arrival should go to ask for a session, or `null` to draw the form. */
export function bounceTo(at: Arrival): string | null {
  if (!at.bridged) return null;
  if (at.requesting) return null;
  /* We are back from an attempt. Without this, two signed-out hosts bounce a browser
     between them for as long as it is open. */
  if (new URLSearchParams(at.search).get(BOUNCED) === BOUNCED_VALUE) {
    return null;
  }
  if (attemptSuppresses(at.attempted, at.now)) return null;
  return startUrl(at.origin, returnPath(at));
}

/** `ATTEMPT_KEY`, or `null` where the jar cannot be read at all. */
export function lastAttempt(): string | null {
  try {
    return window.localStorage.getItem(ATTEMPT_KEY);
  } catch {
    /* A blocked jar asks on every load; the marker still stops it looping. */
    return null;
  }
}

/** Record that the partner was asked, so the next load does not ask again. */
export function recordAttempt(now: number = Date.now()): void {
  try {
    window.localStorage.setItem(ATTEMPT_KEY, String(now));
  } catch {
    /* As above: nothing here is worth failing a sign-in over. */
  }
}

/** The one navigation this module makes, in a box a test can watch (`lib/cluster.ts`). */
export const navigation = {
  go(url: string): void {
    window.location.assign(url);
  },
};

/** Ask the partner for a session. The attempt is recorded BEFORE leaving, or it is not recorded. */
export function askPartner(url: string): void {
  recordAttempt();
  navigation.go(url);
}

/**
 * Sign out of this host and the partner (D6), landing on the admin's own front door and
 * its login form.
 *
 * Started on the PARTNER, which is the whole of how the landing host is chosen. Each hop
 * clears first-party and redirects on, and the hop that carries `next=partner` builds the
 * onward URL from the pairing with no query (MathGaps/schools `services/auth/src/cross.ts`
 * `crossSignOutRoute`) — so the second host sees no `next`, falls through to its own
 * origin, and the chain always ends on whichever host it did NOT start on. Starting here
 * ended on the partner; starting there ends here. Both hosts are cleared either way: the
 * hops are the same two, in the other order.
 *
 * The attempt is recorded first for the reason 3.0 records it: the chain lands signed out,
 * and the next load of the admin must not immediately ask for the session it just
 * deliberately destroyed.
 */
export function crossSignOut(partner: string): void {
  recordAttempt();
  navigation.go(signOutUrl(partner));
}
